import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Download,
  History,
  ImageIcon,
  Loader2,
  Lock,
  Play,
  Terminal,
  UploadCloud,
  X,
  Wand2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DeclinationsPage from "./DeclinationsPage";
import HistoryPage from "./HistoryPage";
import LoginPage from "./LoginPage";
import { ImageModal } from "./ImageModal";
import { ImageEditModal } from "./ImageEditModal";
import { API_URL, BACKEND_URL } from "./backendUrl";

const API = API_URL;

const STATUS_LABELS = {
  idle: "EN ATTENTE",
  pending: "INITIALISATION",
  running: "GÉNÉRATION EN COURS",
  completed: "TERMINÉ",
  partial: "PARTIEL",
  failed: "ÉCHEC",
};

const DEFAULT_TRANSFORMATION_PROMPT = `Tu es un Directeur Artistique Senior spécialisé en Meta Ads, Facebook Ads, Instagram Ads et Display Ads.

Ta mission est de générer UNE variation A/B testing crédible à partir de la création fournie.

OBJECTIF

La variation doit être suffisamment différente pour tester une hypothèse marketing réelle.

L’objectif n’est PAS de reproduire la même création avec quelques éléments déplacés.

L’objectif est de produire une nouvelle version susceptible de générer des performances différentes.

⸻

ÉLÉMENTS À CONSERVER OBLIGATOIREMENT

* Format et ratio d’origine
* Logo de la marque
* Produit principal
* Tous les textes existants
* Tous les prix existants
* Message publicitaire principal
* Identité de marque

Aucun texte ne doit être inventé.

Aucun prix ne doit être modifié.

Aucun logo ne doit être remplacé ou déformé.

⸻

PALETTE DE COULEURS

Détecte automatiquement la palette présente dans la création originale.

Tu peux :

* Inverser les couleurs existantes
* Utiliser une couleur secondaire comme couleur dominante
* Modifier la répartition des couleurs
* Ajuster contraste, luminosité et saturation

Tu ne peux jamais :

* Introduire une nouvelle couleur
* Utiliser une couleur absente de la création source

Toutes les couleurs de la variation doivent provenir exclusivement de la palette originale.

⸻

MODIFICATIONS AUTORISÉES

Tu peux modifier :

* Position du logo
* Position du CTA
* Taille du CTA
* Position de l’accroche
* Taille de l’accroche
* Position du produit principal
* Taille du produit principal
* Hiérarchie visuelle
* Alignement général
* Répartition des espaces vides
* Couleur dominante
* Couleur secondaire dominante
* Contraste global
* Traitement du fond
* Composition générale

⸻

OBLIGATION A/B TESTING

La variation doit modifier au minimum 5 attributs majeurs parmi :

* Position du logo
* Position du CTA
* Taille du CTA
* Position de l’accroche
* Taille de l’accroche
* Position du produit principal
* Taille du produit principal
* Couleur dominante
* Contraste global
* Hiérarchie visuelle
* Alignement général
* Répartition des espaces vides
* Fond

Une variation qui modifie moins de 5 attributs est considérée comme invalide.

⸻

HYPOTHÈSES À TESTER

Choisis implicitement une ou plusieurs hypothèses marketing :

* Un CTA plus visible augmente le taux de clic
* Un produit plus dominant améliore l’attention
* Une hiérarchie plus directe améliore la compréhension
* Une couleur dominante différente améliore l’engagement
* Une composition plus épurée améliore la lisibilité
* Une accroche plus visible améliore l’intérêt

La variation doit refléter une véritable logique marketing.

⸻

CONTRÔLE QUALITÉ

L’image finale doit respecter :

* Texte parfaitement lisible
* Aucun texte déformé
* Aucun logo déformé
* Aucun élément coupé
* Aucun chevauchement incohérent
* Marges équilibrées
* Hiérarchie visuelle claire
* Aucun artefact IA
* Aucun effet de pixellisation
* Aucun cadre parasite
* Aucune image dans l’image

⸻

OBJECTIF FINAL

Produire une création publicitaire professionnelle qui :

* Conserve l’identité de marque
* Conserve le contenu original
* Modifie fortement la composition
* Teste une hypothèse marketing réelle
* Est immédiatement exploitable en campagne publicitaire
* Ressemble au travail d’un directeur artistique senior`;

const Dropzone = ({ file, previewUrl, onFile, onClear, disabled }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      toast.error("Format non supporté — PNG, JPEG ou WebP uniquement");
      return;
    }
    onFile(f);
  };

  if (file && previewUrl) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-slate-200 group" data-testid="image-preview">
        <img src={previewUrl} alt="source" className="w-full h-48 object-cover" />
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <p className="text-white text-xs font-mono truncate">{file.name}</p>
        </div>
        {!disabled && (
          <button
            data-testid="remove-image-button"
            onClick={onClear}
            className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all shadow"
          >
            <X className="w-4 h-4 text-slate-900" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="image-upload-dropzone"
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!disabled) handleFiles(e.dataTransfer.files); }}
      className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer flex flex-col items-center justify-center gap-3 group transition-colors ${
        dragOver ? "border-[#f58207] bg-orange-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        data-testid="image-file-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <UploadCloud className="w-8 h-8 text-slate-400 group-hover:text-[#f58207] transition-colors" />
      <p className="text-sm font-medium text-slate-900">Glissez votre image ici ou cliquez</p>
      <p className="text-xs text-slate-500">PNG, JPEG ou WebP</p>
    </div>
  );
};

const TerminalPanel = ({ logs, status, onDownloadZip }) => {
  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs.length, onDownloadZip]);

  const running = status === "running" || status === "pending";
  const showDownload = (status === "completed" || status === "partial") && typeof onDownloadZip === "function";

  return (
    <div className="h-64 lg:h-80 bg-[#0A0A0A] border-b border-slate-800 flex flex-col shrink-0" data-testid="agent-activity-log">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-black/50 shrink-0">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5" /> Journal d'activité
        </span>
        <span
          data-testid="agent-status-badge"
          className={`px-2 py-1 rounded text-[10px] uppercase font-bold tracking-wider flex items-center gap-1.5 ${
            running
              ? "bg-emerald-500/10 text-[#00FF41]"
              : status === "failed"
              ? "bg-red-500/10 text-red-400"
              : status === "completed"
              ? "bg-emerald-500/10 text-[#00FF41]"
              : "bg-white/5 text-slate-400"
          }`}
        >
          {running && <Loader2 className="w-3 h-3 animate-spin" />}
          {status === "completed" && <CheckCircle2 className="w-3 h-3" />}
          {STATUS_LABELS[status] || status}
        </span>
      </div>
      <div className="p-4 flex-1 overflow-y-auto space-y-2 font-mono text-xs custom-scrollbar">
        {logs.length === 0 ? (
          <p className="text-slate-600">$ En attente d'une mission... Uploadez une image, donnez un titre et lancez la génération.</p>
        ) : (
          logs.map((log, i) => (
            <div key={i} className="flex gap-3 text-slate-300 log-line">
              <span className="text-slate-600 shrink-0 w-16">[{log.time}]</span>
              <span className={`font-medium shrink-0 ${log.action === "ERREUR" ? "text-red-400" : "text-[#00FF41]"}`}>
                [{log.action}]
              </span>
              <span className="text-slate-400 break-all">{log.detail}</span>
            </div>
          ))
        )}
        {showDownload && (
          <button
            data-testid="terminal-zip-download-line"
            onClick={onDownloadZip}
            className="mt-2 flex items-center gap-2 text-[#00FF41] hover:text-white transition-colors font-mono text-xs underline underline-offset-2 log-line"
          >
            <Download className="w-3.5 h-3.5" /> $ telecharger en zip les images
          </button>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

const Gallery = ({ images, expectedCount, status, onImageClick, onEditImage, sourceUrl }) => {
  const displayImages = sourceUrl
    ? [
        {
          index: 0,
          url: sourceUrl,
          label: "Image originale",
          filename: sourceUrl.split("/").pop() || "source.png",
          isOriginal: true,
        },
        ...images,
      ]
    : images;
  const placeholders = status === "running" || status === "pending" ? Math.max(0, expectedCount - images.length) : 0;
  const resolveSrc = (url) => (url?.startsWith("blob:") || url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8" data-testid="results-gallery">
      <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-6 font-heading">
        Galerie des variations
      {displayImages.length > 0 && (
          <span className="ml-3 text-sm font-mono font-normal text-slate-500">
            {images.length}{expectedCount ? `/${expectedCount}` : ""} générées
          </span>
        )}
      </h2>
      {displayImages.length === 0 && placeholders === 0 ? (
        <div className="h-64 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3">
          <ImageIcon className="w-10 h-10" />
          <p className="text-sm">Les images générées apparaîtront ici</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {displayImages.map((img) => (
            <div
              key={img.index}
              role="button"
              tabIndex={0}
              onClick={() => onImageClick(img)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onImageClick(img);
              }}
              className="group relative aspect-square bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm card-reveal text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#f58207]/30"
              data-testid={`result-image-card-${img.index}`}
            >
              <img
                src={resolveSrc(img.url)}
                alt={img.isOriginal ? "Image originale" : `Variation ${img.index}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-xs font-bold px-2 py-1 rounded text-slate-900 shadow-sm">
                {img.isOriginal ? "Originale" : `#${img.index}`}
              </span>
              <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                <div className="flex flex-col items-center gap-2">
                  <span className="bg-white/95 text-slate-900 px-4 py-2 rounded-full text-xs font-bold shadow-xl">
                    Cliquer pour agrandir
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditImage(img);
                    }}
                    className="bg-[#f58207] text-white px-4 py-2 rounded-full text-xs font-bold shadow-xl flex items-center gap-2 hover:bg-[#c86b05] transition-colors"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    {img.isOriginal ? "Modifier l'originale" : "Modifier l'image"}
                  </button>
                </div>
              </div>
            </div>
          ))}
          {Array.from({ length: placeholders }).map((_, i) => (
            <div
              key={`ph-${i}`}
              className="aspect-square bg-slate-100 border border-slate-200 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-2"
            >
              <Loader2 className="w-6 h-6 animate-spin" />
              <p className="text-xs font-mono">en file d'attente...</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [title, setTitle] = useState(DEFAULT_TRANSFORMATION_PROMPT);
  const [iterations, setIterations] = useState("5");
  const [job, setJob] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [editingJobId, setEditingJobId] = useState(null);
  const [view, setView] = useState("generator");
  const [declJob, setDeclJob] = useState(null);
  const [declOrigin, setDeclOrigin] = useState("generator");
  const [promptUnlocked, setPromptUnlocked] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/auth/me`)
      .then(() => {
        if (!cancelled) {
          setAuthed(true);
          setAuthChecked(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAuthed(false);
          setAuthChecked(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    if (!authed && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
    if (authed && location.pathname === "/login") {
      navigate("/", { replace: true });
    }
  }, [authChecked, authed, location.pathname, navigate]);

  const isRunning = launching || job?.status === "pending" || job?.status === "running";
  const jobDone = job && (job.status === "completed" || job.status === "partial") && (job.images?.length || 0) > 0;

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = useCallback((jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/jobs/${jobId}`);
        setJob(res.data);
        if (["completed", "partial", "failed"].includes(res.data.status)) {
          stopPolling();
          if (res.data.status === "completed") toast.success("Mission terminée ! Toutes les images ont été générées.");
          else if (res.data.status === "partial") toast.warning("Mission partiellement terminée — voir le journal.");
          else toast.error("Mission échouée — voir le journal pour les détails.");
        }
      } catch {
        /* keep polling */
      }
    }, 2000);
  }, []);

  useEffect(() => stopPolling, []);

  const handleLogin = async (username, password) => {
    setLoginLoading(true);
    setLoginError("");
    try {
      await axios.post(`${API}/auth/login`, { username, password });
      setAuthed(true);
      navigate("/", { replace: true });
    } catch (e) {
      setAuthed(false);
      setLoginError(e.response?.data?.detail || "Impossible de se connecter");
      throw e;
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      /* ignore */
    }
    stopPolling();
    setAuthed(false);
    setJob(null);
    setDeclJob(null);
    setModalImage(null);
    setEditingImage(null);
    setEditingJobId(null);
    navigate("/login", { replace: true });
  };

  const handleFile = (f) => {
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const ensureSourceJob = async () => {
    if (job?.source_url) {
      return job;
    }
    if (!file) {
      toast.error("Veuillez uploader une image");
      return null;
    }
    const form = new FormData();
    form.append("image", file);
    form.append("title", title.trim());
    form.append("prompt", DEFAULT_TRANSFORMATION_PROMPT);
    try {
      const res = await axios.post(`${API}/jobs/source`, form);
      setJob(res.data);
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de préparer l'image source");
      return null;
    }
  };

  const launchAgent = async () => {
    if (!file) return toast.error("Veuillez uploader une image");
    if (!title.trim()) return toast.error("Veuillez écrire un titre");
    setLaunching(true);
    setJob(null);
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("title", title.trim());
      form.append("prompt", DEFAULT_TRANSFORMATION_PROMPT);
      form.append("iterations", iterations);
      const res = await axios.post(`${API}/jobs`, form);
      setJob(res.data);
      startPolling(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du lancement de la génération");
    } finally {
      setLaunching(false);
    }
  };

  const downloadJobZip = async () => {
    if (!job) return;
    try {
      const res = await fetch(`${API}/jobs/${job.id}/zip`, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `variations_${job.id.slice(0, 8)}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  const openDeclinations = (sourceJob, origin) => {
    setDeclJob(sourceJob);
    setDeclOrigin(origin);
    setView("declinations");
  };

  const openVariationsFromHistory = (historyJob) => {
    if (!historyJob) return;
    stopPolling();
    setJob(historyJob);
    setDeclJob(null);
    setDeclOrigin("generator");
    setView("generator");
  };

  const goToNextStep = async () => {
    if (jobDone && job) {
      openDeclinations(job, "generator");
      return;
    }
    const sourceJob = await ensureSourceJob();
    if (sourceJob) {
      openDeclinations(sourceJob, "generator");
    }
  };

  const declineCurrentImage = async () => {
    const sourceJob = await ensureSourceJob();
    if (sourceJob) {
      openDeclinations(sourceJob, "generator");
    }
  };

  const editCurrentImage = async (image) => {
    if (!image) return;
    if (image.isOriginal && !job?.id) {
      const sourceJob = await ensureSourceJob();
      if (!sourceJob) return;
      setEditingJobId(sourceJob.id);
      setEditingImage({
        ...image,
        index: 0,
        url: sourceJob.source_url,
        isOriginal: true,
      });
      return;
    }
    setEditingJobId(job?.id || null);
    setEditingImage(image);
  };

  const handleImageEdited = (updatedJob, updatedImage) => {
    if (updatedJob) {
      setJob(updatedJob);
      setEditingJobId(updatedJob.id || editingJobId);
    }
  };

  const openMainImage = (image) => {
    setModalImage(image);
  };

  const submitImageEdit = async ({ jobId, prompt, indices }) => {
    if (!jobId || !prompt?.trim()) return null;
    const targetIndices = Array.from(new Set(indices || [])).sort((a, b) => a - b);
    if (targetIndices.length === 0) return null;
    let livePollRef = null;
    const startLiveJobRefresh = () => {
      if (livePollRef) return;
      livePollRef = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/jobs/${jobId}`);
          setJob(res.data);
        } catch {
          /* keep refreshing */
        }
      }, 1200);
    };
    const stopLiveJobRefresh = () => {
      if (livePollRef) {
        clearInterval(livePollRef);
        livePollRef = null;
      }
    };

    startLiveJobRefresh();
    try {
      if (targetIndices.length === 1) {
        const res = await axios.post(`${API}/jobs/${jobId}/images/${targetIndices[0]}/edit`, { prompt: prompt.trim() });
        setJob(res.data.job);
        return res.data.job;
      }

      const res = await axios.post(`${API}/jobs/${jobId}/images/edit-batch`, {
        prompt: prompt.trim(),
        indices: targetIndices,
      });
      setJob(res.data.job);
      return res.data.job;
    } finally {
      stopLiveJobRefresh();
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <div className="flex items-center gap-3 text-sm text-white/70">
          <Loader2 className="w-4 h-4 animate-spin text-[#f58207]" />
          Vérification de l'accès...
        </div>
      </div>
    );
  }

  if (location.pathname === "/login") {
    return (
      <>
        <Toaster position="top-center" richColors />
        <LoginPage
          loading={loginLoading}
          error={loginError}
          onLogin={handleLogin}
        />
      </>
    );
  }

  if (view === "declinations" && declJob) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <DeclinationsPage job={declJob} onBack={() => setView(declOrigin)} onLogout={handleLogout} />
      </>
    );
  }

  if (view === "history") {
    return (
      <>
        <Toaster position="top-center" richColors />
        <HistoryPage
          onBack={() => setView("generator")}
          onDecline={(historyJob) => openDeclinations(historyJob, "history")}
          onOpenVariations={openVariationsFromHistory}
          onLogout={handleLogout}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-200 flex flex-col md:flex-row font-body text-slate-700">
      <Toaster position="top-center" richColors />
      {modalImage && (
        <ImageModal
          image={modalImage}
          onClose={() => setModalImage(null)}
          onDecline={declineCurrentImage}
        />
      )}
      {editingImage && (job?.id || editingImage.isOriginal) && (
        <ImageEditModal
          jobId={editingJobId || job?.id}
          image={editingImage}
          availableImages={[
            ...(job?.source_url
              ? [{ index: 0, url: job.source_url, isOriginal: true, label: "Image originale" }]
              : []),
            ...(job?.images || []),
          ]}
          onClose={() => {
            setEditingImage(null);
            setEditingJobId(null);
          }}
          onSubmitEdit={submitImageEdit}
        />
      )}

      {/* Sidebar */}
      <aside className="w-full md:w-[400px] lg:w-[480px] bg-white border-r border-slate-200 flex flex-col md:h-screen shrink-0">
        <div className="p-6 border-b border-slate-200 bg-white z-10">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                <img src="/logo-gm.png" alt="Logo GM" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-slate-900 font-heading">SaaS DCO</h1>
              </div>
            </div>
            <button
              data-testid="history-button"
              onClick={() => setView("history")}
              title="Historique des générations"
              className="flex items-center gap-2 h-9 px-3 text-xs font-bold text-slate-700 border border-slate-200 rounded-lg hover:border-[#f58207] hover:text-[#f58207] transition-colors shrink-0"
            >
              <History className="w-4 h-4" />
              Historique
            </button>
            <button
              onClick={handleLogout}
              className="text-xs font-medium text-slate-500 hover:text-red-500 transition-colors"
            >
              Déconnexion
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          <div>
            <label className="block text-[11px] font-bold tracking-[0.2em] text-slate-900 mb-2 uppercase">
              1 · Image source
            </label>
            <Dropzone
              file={file}
              previewUrl={previewUrl}
              onFile={handleFile}
              onClear={() => { setFile(null); setPreviewUrl(null); }}
              disabled={isRunning}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[11px] font-bold tracking-[0.2em] text-slate-900 uppercase">
                2 · Prompt
              </label>
              <button
                type="button"
                onClick={() => setPromptUnlocked((v) => !v)}
                className="h-8 px-3 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:border-[#f58207] hover:text-[#f58207] transition-colors"
              >
                {promptUnlocked ? "Verrouiller" : "Déverrouiller"}
              </button>
            </div>
            <div className="relative">
              <textarea
                data-testid="title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                readOnly={isRunning || !promptUnlocked}
                placeholder="Décris la transformation souhaitée..."
                rows={6}
                className={`w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium leading-6 focus:border-[#f58207] focus:ring-1 focus:ring-[#f58207] outline-none transition-colors resize-none ${isRunning || !promptUnlocked ? "opacity-60" : ""}`}
              />
              {!promptUnlocked && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/45 backdrop-blur-[2px] pointer-events-none">
                  <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/85 border border-slate-200 px-5 py-4 shadow-sm">
                    <div className="h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                      <Lock className="h-5 w-5 text-slate-700" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-slate-700">Le prompt est verrouillé par défaut</p>
                      <p className="mt-1 text-[11px] text-slate-500">Clique sur Déverrouiller pour modifier</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Clique sur Déverrouiller pour modifier le prompt.
            </p>
          </div>

          <div>
            <label className="block text-[11px] font-bold tracking-[0.2em] text-slate-900 mb-2 uppercase">
              3 · Nombre de variations
            </label>
            <Select value={iterations} onValueChange={setIterations} disabled={isRunning}>
              <SelectTrigger
                data-testid="iteration-count-select"
                className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["2", "5", "10", "15", "20"].map((n) => (
                  <SelectItem key={n} value={n} data-testid={`iteration-option-${n}`}>
                    {n} variations
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">
              Le système enverra <span className="font-bold text-slate-900">{iterations} fois</span> la même image source avec votre titre.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-slate-200 bg-slate-50 space-y-3">
          {(jobDone || previewUrl || (job?.source_url && !(job?.images?.length || 0))) && (
            <button
              data-testid="next-step-button"
              onClick={goToNextStep}
              className="w-full h-12 bg-white text-[#f58207] border-2 border-[#f58207] font-bold text-sm rounded-xl hover:bg-[#f58207] hover:text-white transition-colors flex items-center justify-center gap-2"
            >
              Passer à l'étape suivante <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="launch-agent-button"
            onClick={launchAgent}
            disabled={isRunning}
            className="w-full h-14 bg-[#f58207] text-white font-bold text-base rounded-xl hover:bg-[#c86b05] transition-colors flex items-center justify-center gap-2 shadow-[0_4px_14px_0_rgba(245,130,7,0.39)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Génération en cours...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" /> Lancer la génération
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Main area */}
      <main className="flex-1 flex flex-col md:h-screen min-w-0 bg-slate-50">
        <TerminalPanel
          logs={job?.logs || []}
          status={job?.status || (launching ? "pending" : "idle")}
          onDownloadZip={jobDone ? downloadJobZip : undefined}
        />
        <Gallery
          images={job?.images || []}
          expectedCount={job?.iterations || 0}
          status={job?.status || "idle"}
          onImageClick={openMainImage}
          onEditImage={editCurrentImage}
          sourceUrl={job?.source_url || previewUrl}
        />
      </main>
    </div>
  );
}
